import { ApolloProvider } from '@apollo/client';
import { ApproveAppDialog } from 'app-governance-lib/components/ApproveAppDialog';
import { ManagementModalPanel } from 'app-governance-lib/components/ManagementModalPanel';
import { createApolloClient } from 'hubspot-apollo-client';
import { CheckCircleIcon } from 'foundations-assets/icons/components/CheckCircleIcon';
import { CogIcon } from 'foundations-assets/icons/components/CogIcon';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApprovalProvider } from 'ui-product-approvals/components/ApprovalProvider';
import { useGetAppGovernancePermissionsQuery, useGetAppsGovernanceStatusQuery } from '../../__generated__/chirp/com/hubspot/applications/governance/rpc/ApplicationGovernanceAppAuthRpc.hooks';
import { useGetUsersByIdsQuery } from '../../__generated__/chirp/com/hubspot/appusers/rpc/UsersRpc.hooks';
import { useRejectApprovalMutation } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { APP_GOVERNANCE_APPROVAL_ACTION, APP_GOVERNANCE_APPROVAL_TYPE } from './constants';
import { useGetLatestProductApprovalRequest } from './hooks/useGetLatestProductApprovalRequest';
import { useGetOrCreateApprovableObjectId } from './hooks/useGetOrCreateApprovableObjectId';
import { useGetProductApprovalConfiguration } from './hooks/useGetProductApprovalConfiguration';
import { getUserHasAppGovernanceWriteScope } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const apolloClient = createApolloClient();
function AppGovernanceAlertContent({
  appId,
  appName,
  approvableObjectId,
  currentUserId
}) {
  var _userInfo$user, _userInfo$user2, _userData$users$;
  const {
    userInfo
  } = useDetailsPageContext();
  const [isAppGovernanceModalOpen, setIsAppGovernanceModalOpen] = useState(false);
  const [isAppApproved, setIsAppApproved] = useState(false);
  const {
    data,
    hasLatestRequestData,
    requestStatus,
    requesterId,
    loading,
    refetch
  } = useGetLatestProductApprovalRequest({
    approvableObjectId
  });
  const {
    data: appGovernancePermissionsData,
    loading: appGovernancePermissionsLoading,
    refetch: refetchAppGovernancePermissionsData
  } = useGetAppGovernancePermissionsQuery({
    variables: {
      appId
    }
  });
  useEffect(() => {
    // if approval exists, should use manage settings modal
    if (!appGovernancePermissionsLoading) {
      setIsAppApproved((appGovernancePermissionsData === null || appGovernancePermissionsData === void 0 ? void 0 : appGovernancePermissionsData.approvedAt) !== undefined);
    }
  }, [appGovernancePermissionsData, appGovernancePermissionsLoading]);
  const adminEmail = userInfo === null || userInfo === void 0 || (_userInfo$user = userInfo.user) === null || _userInfo$user === void 0 ? void 0 : _userInfo$user.email;
  const adminName = userInfo === null || userInfo === void 0 || (_userInfo$user2 = userInfo.user) === null || _userInfo$user2 === void 0 ? void 0 : _userInfo$user2.first_name;
  const [rejectApproval] = useRejectApprovalMutation();
  const {
    data: userData,
    loading: loadingUserData
  } = useGetUsersByIdsQuery({
    variables: {
      userIds: [parseInt(requesterId || '0', 10)]
    },
    skip: requesterId === undefined
  });
  const isActiveRequest = useRef(requestStatus === 'APPROVAL_PENDING');
  useEffect(() => {
    // alert should only show in APPROVED and REJECTED state immediately after the admin has approved/rejected, otherwise hide
    if (isActiveRequest.current === false) {
      isActiveRequest.current = requestStatus === 'APPROVAL_PENDING';
    }
  }, [requestStatus]);
  const requesterEmail = userData === null || userData === void 0 || (_userData$users$ = userData.users[0]) === null || _userData$users$ === void 0 ? void 0 : _userData$users$.email;
  let requestAlertType = 'info';
  let requestAlertTitleKey = 'PENDING';
  if (isActiveRequest.current) {
    switch (requestStatus) {
      case 'APPROVED':
        requestAlertType = 'success';
        requestAlertTitleKey = 'APPROVED';
        break;
      case 'REJECTED':
        requestAlertType = 'warning';
        requestAlertTitleKey = 'REJECTED';
        break;
      case 'APPROVAL_PENDING':
        requestAlertType = 'info';
        requestAlertTitleKey = isAppApproved ? 'PENDING_ACCESS' : 'PENDING_ADD';
        break;
      default:
        break;
    }
  }

  //if active request modal and missing latest request data, do not show modal
  if (isActiveRequest.current && !hasLatestRequestData) {
    return null;
  }
  if (loading || loadingUserData) {
    return null;
  }
  function handleOpenAppGovernanceModal() {
    setIsAppGovernanceModalOpen(true);
  }
  function handleDenyRequest() {
    var _data$approval;
    rejectApproval({
      variables: {
        approvalId: (data === null || data === void 0 || (_data$approval = data.approval) === null || _data$approval === void 0 ? void 0 : _data$approval.id) || 0,
        rejectionNote: unescapedText('detailsPage.appGovernance.rejectionNote', {
          email: adminEmail,
          name: adminName
        })
      }
    }).then(() => refetch()).catch(err => console.error(err));
  }
  function onAppGovernanceModalClosed() {
    setIsAppGovernanceModalOpen(false);
    refetch();
    refetchAppGovernancePermissionsData().catch(err => console.error(err));
  }
  function ApproveButton() {
    return /*#__PURE__*/_jsxs(Button, {
      marginInlineStart: "200",
      use: 'secondary-alt',
      size: "small",
      onClick: handleOpenAppGovernanceModal,
      children: [/*#__PURE__*/_jsx(CheckCircleIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.appGovernance.approveButton"
      })]
    });
  }
  function ManageButton() {
    return /*#__PURE__*/_jsxs(Button, {
      marginInlineStart: "200",
      use: 'tertiary',
      size: "small",
      onClick: handleOpenAppGovernanceModal,
      children: [/*#__PURE__*/_jsx(CogIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.appGovernance.manageButton"
      })]
    });
  }
  function ActiveRequestAlert() {
    return /*#__PURE__*/_jsxs(Alert, {
      type: requestAlertType,
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.appGovernance.requestAlertTitle.${requestAlertTitleKey}`,
        options: {
          email: requesterEmail
        }
      }),
      marginBlockEnd: "500",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.appGovernance.requestAlertContent.${requestAlertTitleKey}`
      }), requestStatus === 'APPROVAL_PENDING' && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ApproveButton, {}), /*#__PURE__*/_jsx(Button, {
          use: "tertiary",
          size: "small",
          onClick: handleDenyRequest,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.appGovernance.denyButton"
          })
        })]
      }), requestStatus === 'APPROVED' && /*#__PURE__*/_jsx(ManageButton, {})]
    });
  }
  function GenericAlert() {
    return /*#__PURE__*/_jsxs(Alert, {
      type: isAppApproved ? 'success' : 'info',
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.appGovernance.genericAlertTitle.${isAppApproved ? 'approved' : 'default'}`
      }),
      marginBlockEnd: "500",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.appGovernance.genericAlertContent.${isAppApproved ? 'approved' : 'default'}`
      }), isAppApproved ? /*#__PURE__*/_jsx(ManageButton, {}) : /*#__PURE__*/_jsx(ApproveButton, {})]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isActiveRequest.current ? /*#__PURE__*/_jsx(ActiveRequestAlert, {}) : /*#__PURE__*/_jsx(GenericAlert, {}), isAppGovernanceModalOpen && /*#__PURE__*/_jsx(ApolloProvider, {
      client: apolloClient,
      children: isAppApproved ? /*#__PURE__*/_jsx(ManagementModalPanel, {
        onClose: onAppGovernanceModalClosed,
        appName: appName,
        appId: appId,
        currentUserId: currentUserId,
        onInstallPermissionsSave: onAppGovernanceModalClosed,
        onRevokeApproval: onAppGovernanceModalClosed
      }) : /*#__PURE__*/_jsx(ApproveAppDialog, {
        type: "modal",
        appId: appId,
        appName: appName,
        currentUserId: currentUserId,
        onClose: onAppGovernanceModalClosed,
        onConfirm: onAppGovernanceModalClosed
      })
    })]
  });
}
export function AppGovernanceAlert({
  appId,
  appName
}) {
  var _userInfo$user$user_i, _userInfo$user3;
  const {
    userInfo,
    appContext
  } = useDetailsPageContext();
  const {
    approvableObjectId
  } = useGetOrCreateApprovableObjectId({
    appId
  });
  const {
    enabled: isProductApprovalsEnabled
  } = useGetProductApprovalConfiguration();
  const {
    data: appGovernanceStatusData
  } = useGetAppsGovernanceStatusQuery({
    variables: {
      appIds: [appId]
    },
    skip: !appId
  });
  const showAppGovernanceAlert = useMemo(() => getUserHasAppGovernanceWriteScope(userInfo) && (appGovernanceStatusData === null || appGovernanceStatusData === void 0 ? void 0 : appGovernanceStatusData.appGovernanceStatuses[appId]) === 'GOVERNED', [appGovernanceStatusData === null || appGovernanceStatusData === void 0 ? void 0 : appGovernanceStatusData.appGovernanceStatuses, appId, userInfo]);
  if (!showAppGovernanceAlert || appContext !== 'app' || !isProductApprovalsEnabled || !approvableObjectId) {
    return null;
  }
  return /*#__PURE__*/_jsx(ApprovalProvider, {
    approvableObjectId: approvableObjectId,
    assetName: appName,
    approvalType: APP_GOVERNANCE_APPROVAL_TYPE,
    approvalAction: APP_GOVERNANCE_APPROVAL_ACTION,
    children: /*#__PURE__*/_jsx(AppGovernanceAlertContent, {
      appId: appId,
      appName: appName,
      approvableObjectId: approvableObjectId,
      currentUserId: (_userInfo$user$user_i = userInfo === null || userInfo === void 0 || (_userInfo$user3 = userInfo.user) === null || _userInfo$user3 === void 0 ? void 0 : _userInfo$user3.user_id) !== null && _userInfo$user$user_i !== void 0 ? _userInfo$user$user_i : 0
    })
  });
}